/**
 * Reading the server's reason out of a failed API call.
 *
 * FastAPI puts the reason in `response.data.detail` (or `.error` for a few
 * handlers). Axios's own `error.message` is a generic string like "Request
 * failed with status code 500", so reaching for it first throws away the only
 * text that tells the user what actually went wrong.
 */

/** The server's explanation, or null when the request never reached it. */
export function serverDetail(error: unknown): string | null {
  const data = (error as any)?.response?.data;
  const detail = data?.detail ?? data?.error;
  // FastAPI validation errors put a list of objects here, not a string.
  // Rendering "[object Object]" helps nobody, so fall through to the generic
  // message in that case.
  return typeof detail === 'string' && detail.trim() ? detail : null;
}

/**
 * The best available message for a failed request.
 *
 * Prefers the server's reason, then Axios's message, then `fallback` for a
 * request that produced neither.
 */
export function apiErrorMessage(error: unknown, fallback: string): string {
  const detail = serverDetail(error);
  if (detail) return detail;
  if (error instanceof Error && error.message) return error.message;
  return fallback;
}
